/**
 * PetalHush Quick view (P-065, founder 2026-10-16).
 *
 * A "Quick view" pill on Horizon product cards (snippets/ph-quick-view.liquid) opens one shared native <dialog>
 * filled from /products/<handle>.js: images, title (split at the first " – " like the PDPs), price, and a single
 * "Make yours" link to the product page. No add-to-cart and no variant picker here: every piece is personalized
 * (photo upload, engraving) on the product page.
 *
 * Event delegation on document, so cards re-rendered by Horizon (filters, sort, pagination) work without re-binding.
 * Reuses Horizon modules from the import map: money formatting, scroll lock and the scroll container.
 * Horizon's quick-add.js is not used or changed.
 */
import { formatMoney } from '@theme/money-formatting';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

if (!window.__phQuickView) {
  window.__phQuickView = true;

  const TRIGGER = '[data-ph-qv]';
  const IMG_WIDTHS = [360, 540, 720, 960, 1200];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {{moneyFormat?: string, currency?: string, t?: Record<string, string>}} */
  let cfg = {};
  try {
    cfg = JSON.parse(document.getElementById('ph-qv-config')?.textContent || '{}');
  } catch (e) {
    cfg = {};
  }
  const t = Object.assign(
    {
      close: 'Close dialog',
      prev: 'Previous slide',
      next: 'Next slide',
      slideshow: 'Slideshow',
      slide: 'Slide __I__ of __N__',
      from: 'From __P__',
      price: 'Price',
      regular: 'Original price',
    },
    cfg.t || {}
  );

  /** @type {Map<string, Promise<any>>} */
  const cache = new Map();

  const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /** Fetch a product once; failed requests are dropped from the cache so a retry can work. */
  function load(handle) {
    if (!handle) return Promise.reject(new Error('no handle'));
    let p = cache.get(handle);
    if (!p) {
      p = fetch(`${routesRoot()}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      });
      p.catch(() => cache.delete(handle));
      cache.set(handle, p);
    }
    return p;
  }

  function money(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || cfg.currency || 'USD';
    const format = (cfg.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    try {
      return formatMoney(cents, format, currency);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  /** Same split as the PDP title (P-056b): the part after the first " – " is a smaller grey line. */
  function splitTitle(title) {
    const s = String(title || '');
    const i = s.indexOf(' – ');
    if (i > 0) {
      const main = s.slice(0, i).trim();
      const sub = s.slice(i + 3).trim();
      if (main && sub) return [main, sub];
    }
    return [s, ''];
  }

  function sized(src, w) {
    if (!src) return '';
    const u = src.startsWith('//') ? `https:${src}` : src;
    return u + (u.includes('?') ? '&' : '?') + `width=${w}`;
  }

  function slideLabel(i, n) {
    return t.slide.replace('__I__', String(i)).replace('__N__', String(n));
  }

  const ICON_CLOSE =
    '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/></svg>';
  const ICON_PREV =
    '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><path d="M12.5 4.5L7 10l5.5 5.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';
  const ICON_NEXT =
    '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><path d="M7.5 4.5L13 10l-5.5 5.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';

  /** @type {HTMLDialogElement | null} */
  let dlg = null;
  /** @type {Record<string, HTMLElement>} */
  let el = {};
  const state = { idx: 0, n: 0, token: 0, scrollY: 0, trigger: /** @type {HTMLElement | null} */ (null), downOnBackdrop: false };

  function esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  function build() {
    dlg = document.createElement('dialog');
    dlg.className = 'ph-qv';
    dlg.setAttribute('aria-labelledby', 'ph-qv-title');
    dlg.innerHTML = `
      <div class="ph-qv__panel">
        <button type="button" class="ph-qv__close" aria-label="${esc(t.close)}" autofocus>${ICON_CLOSE}</button>
        <div class="ph-qv__media">
          <div class="ph-qv__stage">
            <div class="ph-qv__track" tabindex="0" role="group" aria-label="${esc(t.slideshow)}"></div>
            <button type="button" class="ph-qv__arrow ph-qv__arrow--prev" aria-label="${esc(t.prev)}">${ICON_PREV}</button>
            <button type="button" class="ph-qv__arrow ph-qv__arrow--next" aria-label="${esc(t.next)}">${ICON_NEXT}</button>
            <div class="ph-qv__dots" aria-hidden="true"></div>
          </div>
          <div class="ph-qv__thumbs"></div>
          <p class="ph-qv-sr ph-qv__status" aria-live="polite"></p>
        </div>
        <div class="ph-qv__info">
          <h2 class="ph-qv__title" id="ph-qv-title"><span class="ph-qv__name"></span><span class="ph-qv__sub"></span></h2>
          <p class="ph-qv__price"></p>
          <a class="ph-qv__cta" href="#"></a>
        </div>
      </div>`;
    document.body.appendChild(dlg);
    const q = (s) => /** @type {HTMLElement} */ (dlg.querySelector(s));
    el = {
      close: q('.ph-qv__close'),
      track: q('.ph-qv__track'),
      prev: q('.ph-qv__arrow--prev'),
      next: q('.ph-qv__arrow--next'),
      dots: q('.ph-qv__dots'),
      thumbs: q('.ph-qv__thumbs'),
      status: q('.ph-qv__status'),
      name: q('.ph-qv__name'),
      sub: q('.ph-qv__sub'),
      price: q('.ph-qv__price'),
      cta: q('.ph-qv__cta'),
    };

    el.close.addEventListener('click', () => dlg.close());
    el.prev.addEventListener('click', () => goTo(state.idx - 1, true));
    el.next.addEventListener('click', () => goTo(state.idx + 1, true));
    el.thumbs.addEventListener('click', (e) => {
      const b = /** @type {HTMLElement} */ (e.target).closest('[data-i]');
      if (b) goTo(Number(b.getAttribute('data-i')), true);
    });
    el.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(state.idx + 1, true);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(state.idx - 1, true);
      }
    });
    let raf = 0;
    el.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const w = el.track.clientWidth || 1;
          const i = Math.round(Math.abs(el.track.scrollLeft) / w);
          if (i !== state.idx) setActive(i);
        });
      },
      { passive: true }
    );

    // Overlay click closes; only when the press also started on the backdrop (a swipe that ends outside must not close).
    dlg.addEventListener('pointerdown', (e) => {
      state.downOnBackdrop = e.target === dlg;
    });
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg && state.downOnBackdrop) dlg.close();
      state.downOnBackdrop = false;
    });

    // Keep Tab inside the dialog (the page behind is already inert while showModal() is open).
    dlg.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const f = Array.from(dlg.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')).filter(
        (x) => /** @type {HTMLElement} */ (x).offsetParent !== null || x === el.close
      );
      if (!f.length) return;
      const first = /** @type {HTMLElement} */ (f[0]);
      const last = /** @type {HTMLElement} */ (f[f.length - 1]);
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    dlg.addEventListener('close', () => {
      state.token++;
      unlockScroll(dlg);
      scrollTo({ top: state.scrollY, behavior: 'instant' });
      const trg = state.trigger;
      state.trigger = null;
      if (trg && trg.isConnected) trg.focus({ preventScroll: true });
    });
  }

  function setTitle(title) {
    const [main, sub] = splitTitle(title);
    el.name.textContent = main;
    el.sub.innerHTML = sub ? `<span class="ph-qv-sr"> – </span>${esc(sub)}` : '';
    el.sub.hidden = !sub;
  }

  function setPrice(p, variantId) {
    const variants = Array.isArray(p.variants) ? p.variants : [];
    const chosen = variantId ? variants.find((v) => String(v.id) === String(variantId)) : null;
    let price;
    let compare;
    let from = false;
    if (chosen) {
      price = chosen.price;
      compare = chosen.compare_at_price;
    } else {
      price = p.price_min != null ? p.price_min : p.price;
      from = !!p.price_varies;
      // compare-at of the cheapest variant (the one the "From" price belongs to), preferring an available one
      const cheapest =
        variants.find((v) => v.price === price && v.available) || variants.find((v) => v.price === price) || null;
      compare = cheapest ? cheapest.compare_at_price : p.compare_at_price;
    }
    if (price == null) {
      el.price.textContent = '';
      return;
    }
    priceText(money(price), compare && compare > price ? money(compare) : '', from);
  }

  /** Writes the price line from already formatted strings (JSON prices, or the card's Liquid `money` output). */
  function priceText(now, was, from) {
    if (!now) {
      el.price.textContent = '';
      return;
    }
    let html = `<span class="ph-qv-sr">${esc(t.price)} </span><span class="ph-qv__now">${esc(
      from ? t.from.replace('__P__', now) : now
    )}</span>`;
    if (was) html += ` <s class="ph-qv__was"><span class="ph-qv-sr">${esc(t.regular)} </span>${esc(was)}</s>`;
    el.price.innerHTML = html;
  }

  /**
   * What the card already knows, shown the moment the sheet opens (and kept if the JSON fails):
   * featured image + price from the pill's data attributes (Liquid), else the card's own <img>.
   */
  function preview(btn) {
    const d = btn.dataset;
    let items = [];
    if (d.phQvImg) {
      items = [{
        id: d.phQvImgId ? Number(d.phQvImgId) : null,
        src: d.phQvImg,
        w: Number(d.phQvImgW) || 1200,
        h: Number(d.phQvImgH) || 1200,
        alt: d.phQvImgAlt || d.phQvTitle || '',
      }];
    } else {
      const ci = /** @type {HTMLImageElement | null} */ (btn.closest('.card-gallery, product-card')?.querySelector('img'));
      const src = ci && (ci.currentSrc || ci.src);
      if (src) {
        items = [{ id: null, src: src.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, ''), w: ci.naturalWidth || 1200,
          h: ci.naturalHeight || 1200, alt: ci.alt || d.phQvTitle || '' }];
      }
    }
    return { items, now: d.phQvPrice || '', was: d.phQvWas || '', from: 'phQvFrom' in d };
  }

  /** Product images only (no video/3D posters), alt from the media alt or the product title. */
  function mediaList(p) {
    const list = [];
    const media = Array.isArray(p.media) ? p.media : [];
    for (const m of media) {
      if (m.media_type !== 'image') continue;
      const src = (m.preview_image && m.preview_image.src) || m.src;
      if (!src) continue;
      list.push({
        id: m.id,
        src,
        w: (m.preview_image && m.preview_image.width) || m.width || 1200,
        h: (m.preview_image && m.preview_image.height) || m.height || 1200,
        alt: m.alt || p.title || '',
      });
    }
    if (!list.length && Array.isArray(p.images)) {
      for (const src of p.images) list.push({ id: null, src, w: 1200, h: 1200, alt: p.title || '' });
    }
    return list;
  }

  function renderGallery(items, start) {
    const n = items.length;
    state.n = n;
    state.idx = -1;
    /** @type {Map<string, HTMLElement>} */
    const keep = new Map();
    for (const x of Array.from(el.track.children)) {
      const mid = /** @type {HTMLElement} */ (x).dataset.mid;
      if (mid) keep.set(mid, /** @type {HTMLElement} */ (x));
    }
    el.track.replaceChildren();
    el.thumbs.replaceChildren();
    el.dots.replaceChildren();
    dlg.classList.toggle('ph-qv--single', n <= 1);
    items.forEach((m, i) => {
      const reuse = m.id != null ? keep.get(String(m.id)) : undefined;
      if (reuse) keep.delete(String(m.id));
      const slide = reuse || document.createElement('div');
      slide.className = 'ph-qv__slide';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', slideLabel(i + 1, n));
      if (m.id != null) slide.dataset.mid = String(m.id);
      if (reuse) {
        el.track.appendChild(slide);
        addThumb(m, i, n);
        return;
      }
      const img = new Image();
      img.alt = m.alt;
      img.width = m.w;
      img.height = m.h;
      img.decoding = 'async';
      img.loading = i === start ? 'eager' : 'lazy';
      if (i === start) img.setAttribute('fetchpriority', 'high');
      const widths = IMG_WIDTHS.filter((w) => w < m.w);
      widths.push(Math.min(m.w, 1200));
      img.srcset = Array.from(new Set(widths))
        .map((w) => `${sized(m.src, w)} ${w}w`)
        .join(', ');
      img.sizes = '(min-width: 750px) 484px, 100vw';
      img.src = sized(m.src, Math.min(m.w, 720));
      slide.appendChild(img);
      el.track.appendChild(slide);
      addThumb(m, i, n);
    });
    if (n) setActive(Math.max(0, Math.min(start || 0, n - 1)), true);
  }

  /** Thumbnail (desktop) + dot (mobile) for slide i; none for a single image. */
  function addThumb(m, i, n) {
    if (n > 1) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ph-qv__thumb';
      b.setAttribute('data-i', String(i));
      b.setAttribute('aria-label', slideLabel(i + 1, n));
      const ti = new Image();
      ti.alt = '';
      ti.width = 104;
      ti.height = Math.round((104 * m.h) / m.w) || 104;
      ti.loading = 'lazy';
      ti.decoding = 'async';
      ti.src = sized(m.src, 120);
      b.appendChild(ti);
      el.thumbs.appendChild(b);
      el.dots.appendChild(document.createElement('span'));
    }
  }

  function setActive(i, silent) {
    if (!state.n) return;
    i = Math.max(0, Math.min(i, state.n - 1));
    state.idx = i;
    el.thumbs.querySelectorAll('.ph-qv__thumb').forEach((b, k) => {
      if (k === i) {
        b.setAttribute('aria-current', 'true');
        // keep the current thumbnail in view inside the strip (never scrolls the page or dialog)
        const s = /** @type {HTMLElement} */ (el.thumbs);
        const bx = /** @type {HTMLElement} */ (b);
        const left = bx.offsetLeft; // the strip is position: relative, so this is relative to it
        if (left < s.scrollLeft + 24 || left + bx.offsetWidth > s.scrollLeft + s.clientWidth - 24) {
          s.scrollTo({ left: Math.max(0, left - s.clientWidth / 2 + bx.offsetWidth / 2), behavior: reduceMotion.matches ? 'instant' : 'smooth' });
        }
      } else b.removeAttribute('aria-current');
    });
    el.dots.querySelectorAll('span').forEach((d, k) => d.classList.toggle('is-on', k === i));
    /** @type {HTMLButtonElement} */ (el.prev).disabled = i <= 0;
    /** @type {HTMLButtonElement} */ (el.next).disabled = i >= state.n - 1;
    if (!silent && state.n > 1) el.status.textContent = slideLabel(i + 1, state.n);
  }

  function goTo(i, smooth) {
    if (!state.n) return;
    i = Math.max(0, Math.min(i, state.n - 1));
    const w = el.track.clientWidth;
    el.track.scrollTo({ left: i * w, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'instant' });
    setActive(i);
  }

  function fill(p, url) {
    let variantId = null;
    try {
      variantId = new URL(url, window.location.origin).searchParams.get('variant');
    } catch (e) {
      variantId = null;
    }
    setTitle(p.title || '');
    setPrice(p, variantId);
    const items = mediaList(p);
    let start = 0;
    if (variantId && Array.isArray(p.variants)) {
      const v = p.variants.find((x) => String(x.id) === String(variantId));
      const mid = v && v.featured_media && v.featured_media.id;
      if (mid) start = Math.max(0, items.findIndex((m) => m.id === mid));
    }
    renderGallery(items, start);
    el.track.scrollLeft = start * el.track.clientWidth;
    dlg.classList.remove('ph-qv--loading');
    dlg.classList.toggle('ph-qv--noimg', !items.length);
    dlg.removeAttribute('aria-busy');
  }

  function cardUrl(btn) {
    let url = btn.getAttribute('data-ph-qv-url') || '';
    const card = /** @type {any} */ (btn.closest('product-card'));
    try {
      // Horizon's ProductCard keeps a swatch-selected variant in its product page URL
      if (card && typeof card.productPageUrl === 'string' && card.productPageUrl) url = card.productPageUrl;
    } catch (e) {
      /* keep the Liquid URL */
    }
    return url;
  }

  function open(btn) {
    if (!dlg) build();
    if (dlg.open) return;
    const handle = btn.getAttribute('data-ph-qv') || '';
    const url = cardUrl(btn);
    const token = ++state.token;
    state.trigger = btn;

    setTitle(btn.getAttribute('data-ph-qv-title') || '');
    el.cta.textContent = btn.getAttribute('data-ph-qv-cta') || 'Make yours';
    /** @type {HTMLAnchorElement} */ (el.cta).href = url;
    const pv = preview(btn);
    priceText(pv.now, pv.was, pv.from);
    el.track.replaceChildren(); // never reuse another product's slide
    renderGallery(pv.items, 0);
    dlg.classList.remove('ph-qv--error', 'ph-qv--noimg');
    dlg.classList.add('ph-qv--loading');
    dlg.setAttribute('aria-busy', 'true');

    state.scrollY = getScrollTop();
    lockScroll(dlg);
    dlg.showModal();
    el.close.focus({ preventScroll: true });

    load(handle).then(
      (p) => {
        if (token === state.token && dlg.open) fill(p, url);
      },
      () => {
        // JSON failed: keep the card's image, title and price from the preview; "Make yours" still goes to the PDP
        if (token !== state.token) return;
        dlg.classList.remove('ph-qv--loading');
        dlg.classList.add('ph-qv--error');
        dlg.classList.toggle('ph-qv--noimg', !state.n);
        dlg.removeAttribute('aria-busy');
      }
    );
  }

  document.addEventListener('click', (e) => {
    const target = /** @type {Element | null} */ (e.target instanceof Element ? e.target : null);
    const btn = target && target.closest(TRIGGER);
    if (!btn) return;
    e.preventDefault();
    open(/** @type {HTMLElement} */ (btn));
  });

  // Warm the cache when the pill is hovered, touched or focused.
  const warm = (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const btn = target && target.closest(TRIGGER);
    if (btn) load(btn.getAttribute('data-ph-qv') || '').catch(() => {});
  };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm);
}
